import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type {
	useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$data,
	useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$variables,
} from './__generated__/useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery.graphql.ts';
import type {
	useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$data,
	useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$variables,
} from './__generated__/useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery.graphql.ts';

interface UseIsEligibleReactionsFeedbackPageReturnResponse {
	isEligible: boolean;
	loading: boolean;
	error?: Error;
}

const countReactionsByTime = graphql`
	query useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery(
		$pageId: [ID!]!
		$startTime: String!
	) {
		countGroupByPage(
			pageId: $pageId
			startTime: $startTime
			eventName: [reaction_created]
			limit: 1
		) {
			nodes {
				count
				page
			}
		}
	}
`;

const getUserLastViewed = graphql`
	query useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery(
		$contentIds: [ID!]!
		$startTime: String!
		$endTime: String
		$accountIds: [ID!]
	) {
		contentAnalyticsLastViewedAtByPage(
			contentIds: $contentIds
			startTime: $startTime
			endTime: $endTime
			accountIds: $accountIds
		) {
			nodes {
				contentId
				lastViewedAt
			}
		}
	}
`;

export const useIsEligibleReactionsFeedbackPageReturn = ({
	contentId,
	isContributor,
	skip = false,
}: {
	contentId: string;
	isContributor: boolean;
	skip?: boolean;
}): UseIsEligibleReactionsFeedbackPageReturnResponse => {
	const thirtyDaysAgo = useMemo(() => {
		const date = new Date();
		date.setDate(date.getDate() - 30);
		return date.toISOString();
	}, []);

	// To prevent counting the current visit
	const fiveSecondsAgo = useMemo(() => {
		const date = new Date();
		date.setSeconds(date.getSeconds() - 5);
		return date.toISOString();
	}, []);

	const { userId } = useSessionData();

	const shouldRunUserLastViewedQuery = isContributor && !skip && userId;

	const {
		data: userLastViewedData,
		loading: userLastViewedLoading,
		error: userLastViewedError,
	} = useQueryInterop<
		useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$data,
		useIsEligibleReactionsFeedbackPageReturnGetUserLastViewedQuery$variables
	>(getUserLastViewed, {
		variables: {
			contentIds: [contentId],
			startTime: thirtyDaysAgo,
			endTime: fiveSecondsAgo,
			accountIds: userId ? [userId] : [],
		},
		skip: !shouldRunUserLastViewedQuery,
	});

	const lastViewedAt = useMemo(() => {
		return userLastViewedData?.contentAnalyticsLastViewedAtByPage?.nodes?.[0]?.lastViewedAt;
	}, [userLastViewedData]);

	const shouldRunReactionCountQuery = Boolean(
		shouldRunUserLastViewedQuery && !userLastViewedLoading && !userLastViewedError,
	);

	const reactionQueryStartTime = lastViewedAt || thirtyDaysAgo;

	const {
		data: countReactionByTimeData,
		loading: countReactionByTimeLoading,
		error: countReactionByTimeError,
	} = useQueryInterop<
		useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$data,
		useIsEligibleReactionsFeedbackPageReturnReactionCountByTimeQuery$variables
	>(countReactionsByTime, {
		variables: {
			pageId: [contentId],
			startTime: reactionQueryStartTime,
		},
		skip: !shouldRunReactionCountQuery,
	});

	const isLoading = countReactionByTimeLoading || userLastViewedLoading;
	const allErrors = countReactionByTimeError || userLastViewedError;

	const isEligible = useMemo(() => {
		if (skip || !isContributor) {
			return false;
		}

		if (isLoading || allErrors) {
			return false;
		}

		const reactionCount = countReactionByTimeData?.countGroupByPage?.nodes?.[0]?.count || 0;
		return reactionCount > 0;
	}, [countReactionByTimeData, skip, isContributor, isLoading, allErrors]);

	return {
		isEligible,
		loading: isLoading,
		error: allErrors,
	};
};
